import dayjs, { Dayjs } from 'dayjs';

import { Organization } from 'models/entities/organization';

import { CreateGql, UpdateGql, DeleteGql } from './gql';

type Data = {
  id?: string;
  type?: string;
  parentId?: string;
  organizationId: string;
  name?: string;
  userCount?: number;
  createdAt?: string;
  updatedAt?: string;
};

type DataToEdit = {
  name?: string;
};

type DataToSave = {
  type: string;
  organizationId: string;
  name: string;
};

class Store implements Organization {

  readonly id: string;
  readonly type: string;
  readonly parentId?: string;
  readonly organizationId: string;
  readonly name: string;
  readonly userCount: number;
  readonly createdAt?: Dayjs;
  readonly updatedAt?: Dayjs;

  constructor(data: Data) {
    this.id = data.id ?? '';
    this.type = 'Store';
    this.parentId = data.parentId ?? '';
    this.organizationId = data.organizationId;
    this.name = data.name ?? '';
    this.userCount = data.userCount ?? 0;
    this.createdAt = data.createdAt ? dayjs(data.createdAt) : undefined;
    this.updatedAt = data.updatedAt ? dayjs(data.updatedAt) : undefined;
  }

  edit(data: DataToEdit): this {
    return Object.assign(Object.create(this.constructor.prototype), { ...this, ...data });
  }

  validate(): boolean {
    if (!this.name) return false;
    return true;
  }

  async save(): Promise<Store> {
    if (!this.validate()) throw new Error('invalid cache object');
    return this.id ? this.update() : this.create();
  }

  private async create(): Promise<Store> {
    const gql = await new CreateGql().fetch({ organizationId: this.organizationId, input: this.getDataToSave() });
    if (!gql.document) throw new Error('invalid document');
    return new Store(gql.document);
  }

  private async update(): Promise<Store> {
    if (!this.id) throw new Error('invalid relationship');
    const gql = await new UpdateGql().fetch({ id: this.id, input: this.getDataToSave() });
    if (!gql.document) throw new Error('invalid document');
    return new Store(gql.document);
  }

  private getDataToSave(): DataToSave {
    const { type, organizationId, name } = this;
    return { type, organizationId, name };
  }

  async delete(): Promise<Store> {
    if (!this.id) throw new Error('invalid relationship');
    const gql = await new DeleteGql().fetch({ id: this.id });
    if (!gql.document) throw new Error('invalid document');
    return new Store(gql.document);
  }

  setUserCount(userCount: number): this {
    return Object.assign(Object.create(this.constructor.prototype), { ...this, userCount });
  }

  incrementUserCount(): this {
    return this.setUserCount(this.userCount + 1);
  }

  toJSON(): Data {
    const { id, organizationId, name } = this;
    const createdAt = this.createdAt?.toJSON();
    const updatedAt = this.updatedAt?.toJSON();
    return { id, organizationId, name, createdAt, updatedAt };
  }

  static sort(businesses: Store[]) {
    function orderByOrder(a: Store, b: Store) {
      if (a.name.toString() < b.name.toString()) return -1;
      else if (a.name.toString() > b.name.toString()) return 1;
      else return 0;
    }
    return businesses.sort(orderByOrder);
  }

}

export { Store };
export type {
  Data as StoreData,
  DataToSave as StoreDataToSave,
};

export * from './collection';